import { computed, inject, Injectable, signal } from '@angular/core';
import {
  Auth,
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  updatePassword,
} from '@angular/fire/auth';
import * as Sentry from '@sentry/angular';
import { jwtDecode } from 'jwt-decode';
import { from, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Tenant } from 'src/environments/evironment.interface';
import { AccountDataService } from 'src/shared/services/account.data.service/account.data.service';
import { CookieService } from '../cookies/cookie_service';
import { RemoteConfigService } from '../remote-config/remote-config.service';
import { JWT } from './jwt.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  accountDataService = inject(AccountDataService);
  remoteConfigService = inject(RemoteConfigService);
  cookieService = inject(CookieService);
  afAuth = inject(Auth);

  userEmail = signal(this.afAuth.currentUser?.email || '');
  isStaff = computed(() => this.isStaffEmail(this.userEmail()));
  loggedIn = signal<boolean>(this.isLoggedIn());
  isVerified = signal<boolean>(this.afAuth.currentUser?.emailVerified ?? false);
  jwt = signal<JWT | null>(null);
  tenant = signal<Tenant | undefined>(undefined);
  entitlementProvider = signal<string | undefined>(undefined);

  constructor() {
    if (this.isLoggedIn()) {
      this.setLoggedInCookie();
    }

    if (!this.tenant()) {
      this.updateTenant();
    }

    if (!this.entitlementProvider()) {
      this.updateEntitlementProvider();
    }

    // Keep app session (cookie / loggedIn) in sync with Firebase. LoginGuard
    // trusts the loggedIn cookie, so a Firebase-only signOut (e.g. token
    // revocation via AuthStateService) must clear it or the user stays "in".
    this.afAuth.onAuthStateChanged((user) => {
      if (user) {
        this.isVerified.set(user.emailVerified);
        void this.refreshJwt();
      } else {
        this.isVerified.set(false);
        if (this.loggedIn()) {
          this.clearLocalSession();
        }
      }
    });
  }

  // get currentUserJWTUIDFromFirebase
  getUID(): string {
    return this.afAuth.currentUser?.uid ?? '';
  }

  async decodeBearerToken(forceRefresh = false): Promise<JWT | null> {
    const token = await this.getBearerToken(forceRefresh);
    if (!token) {
      return null;
    }

    const decodedToken = jwtDecode(token);
    return decodedToken as JWT;
  }

  async refreshJwt(forceRefresh = false): Promise<void> {
    const decoded = await this.decodeBearerToken(forceRefresh);
    this.jwt.set(decoded);
  }

  setTenant(tenant: string) {
    localStorage.setItem('tenant', tenant);
    this.updateTenant();
  }

  setEntitlementProvider(provider: string) {
    localStorage.setItem('entitlement-provider', provider);
  }

  clearEntitlementProvider() {
    localStorage.removeItem('entitlement-provider');
  }

  setWeightUnit(unit: string) {
    localStorage.setItem('weight-unit', unit);
  }

  getWeightUnit(): string {
    return localStorage.getItem('weight-unit') ?? 'KG';
  }

  getTenantId(): string {
    const savedTenant = localStorage.getItem('tenant');
    if (savedTenant && environment.tenants.some((t) => t.friendly_id === savedTenant)) {
      return savedTenant;
    } else {
      localStorage.removeItem('tenant');
      return environment.defaulttenant;
    }
  }

  getAllTenants(): Tenant[] {
    return environment.tenants;
  }

  getActiveTenant(skipUpdate: boolean = false): Tenant | undefined {
    const id = this.getTenantId();
    let tenant: Tenant | undefined;
    environment.tenants.forEach((t: Tenant) => {
      if (t.friendly_id == id) {
        tenant = t;
      }
    });
    if (skipUpdate) return tenant;
    return tenant;
  }

  getApp(): string {
    return environment.app;
  }

  /**
   * Uses the injected Auth rather than getAuth(): the latter is an AngularFire
   * call, and login runs it outside an injection context, which warns and can
   * destabilise change detection.
   */
  setTenantAndGetAuth(): Auth {
    const auth = this.afAuth;
    const tenant = this.getActiveTenant();
    if (tenant == undefined || tenant == null) throw Error('Tenant not set');
    auth.tenantId = tenant.id;
    this.tenant.set(tenant);
    return auth;
  }

  /**
   * Firebase renews a token that is expiring on its own, so only force a network
   * round-trip when the caller needs claims that were granted server-side since
   * the current token was issued.
   */
  async getBearerToken(forceRefresh = false): Promise<string> {
    const user = this.afAuth.currentUser;
    if (!user) {
      console.error('User not logged in');
      return '';
    }
    const token = await user.getIdToken(forceRefresh);
    return token || '';
  }

  async loginWithEmailAndPassword(email: string, password: string) {
    await signInWithEmailAndPassword(this.setTenantAndGetAuth(), email, password)
      .then(() => {
        this.setLoggedInCookie();
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  async sendPasswordResetEmail(email: string) {
    await sendPasswordResetEmail(this.setTenantAndGetAuth(), email)
      .then(() => {})
      .catch((error) => {
        this.handleError(error);
      });
  }

  async createAccount(email: string, password: string) {
    await createUserWithEmailAndPassword(this.setTenantAndGetAuth(), email, password)
      .then(() => {
        this.setLoggedInCookie();
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  async loginWithGoogle() {
    await signInWithPopup(this.setTenantAndGetAuth(), new GoogleAuthProvider())
      .then(() => {
        this.setLoggedInCookie();
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  async loginWithGoogleWithRedirect() {
    await signInWithRedirect(this.setTenantAndGetAuth(), new GoogleAuthProvider())
      .then(() => {
        this.setLoggedInCookie();
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  async loginWithFacebook() {
    await signInWithPopup(this.setTenantAndGetAuth(), new FacebookAuthProvider())
      .then(() => {
        this.setLoggedInCookie();
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  setLoggedInCookie() {
    this.cookieService.setCookie('loggedIn', 'true', 1);
    this.setSentryUser();
    this.userEmail.set(this.afAuth.currentUser?.email || '');
    this.loggedIn.set(true);
    void this.refreshJwt();
    if (!this.remoteConfigService.initialized() && !this.remoteConfigService.loading()) {
      this.remoteConfigService.setConfigurations();
    }
  }

  removeLoggedInCookie() {
    Sentry.setUser(null);
    this.loggedIn.set(false);
    this.cookieService.removeCookie('loggedIn');
  }

  setSentryUser() {
    Sentry.setUser({
      email: this.afAuth.currentUser?.email ?? '',
      username: this.afAuth.currentUser?.displayName ?? '',
      id: this.getUID(),
    });
  }

  async loginWithApple(auth: Auth) {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    provider.setCustomParameters({
      // "redirect_uri": environment.appleRedirectUrl
    });
    await signInWithPopup(auth, provider)
      .then(() => {
        this.setLoggedInCookie();
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  isLoggedIn(): boolean {
    return this.cookieService.getCookie('loggedIn') == 'true';
  }

  /**
   * Reloads the current user's token and updates the isVerified signal.
   * Call this after the user clicks the email verification link to refresh their verification status.
   * @returns Promise<boolean> - The current email verification status
   */
  async reloadAndCheckVerification(): Promise<boolean> {
    const user = this.afAuth.currentUser;
    if (!user) {
      this.isVerified.set(false);
      return false;
    }

    // Reload the user to get the latest email verification status from Firebase
    await user.reload();
    // Force token refresh to get updated claims
    await user.getIdToken(true);
    const verified = user.emailVerified;
    this.isVerified.set(verified);
    await this.refreshJwt();
    return verified;
  }
  changePassword(newPassword: string) {
    const user = this.afAuth.currentUser;
    if (!user) {
      return throwError(() => new Error('You must be signed in to continue'));
    }
    return from(updatePassword(user, newPassword));
  }

  /**
   * Re-authenticate the current user with email and password.
   * Use before sensitive operations (e.g. signing a form).
   * @throws Error with user-friendly message on failure
   */
  async reauthenticateWithEmailAndPassword(password: string): Promise<void> {
    const auth = this.setTenantAndGetAuth();

    const user = await auth.currentUser;

    if (!user?.email) {
      throw new Error('You must be signed in to continue');
    }

    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);
  }

  /**
   * Re-authenticate the current user with a popup (e.g. Google, Facebook).
   * Use for users who signed in with a provider that does not use password.
   */
  async reauthenticateWithPopup(provider: GoogleAuthProvider | FacebookAuthProvider | OAuthProvider): Promise<void> {
    const auth = this.setTenantAndGetAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error('You must be signed in to continue');
    }
    await reauthenticateWithPopup(user, provider).catch((error) => {
      this.handleError(error);
    });
  }

  /**
   * True if the current user signed in with email/password (so we can prompt for password).
   */
  isEmailPasswordUser(): boolean {
    const user = this.afAuth.currentUser;
    if (!user?.providerData) return false;
    return user.providerData.some((p) => p?.providerId === 'password');
  }

  async logout() {
    await this.afAuth.signOut();
    this.clearLocalSession();
  }

  /** Clears cookie / local app session without touching Firebase Auth. */
  clearLocalSession() {
    this.clearLocalStorage();
    this.removeLoggedInCookie();
    this.jwt.set(null);
    this.entitlementProvider.set(undefined);
  }

  clearLocalStorage() {
    localStorage.removeItem('userID');
    localStorage.removeItem('activePetId');
    localStorage.removeItem('activePetName');
    localStorage.removeItem('dynamicRedirect');
    localStorage.removeItem('tenant');
    localStorage.removeItem('scsession');
    localStorage.removeItem('scalgorithmid');
    localStorage.removeItem('publisherVideoBackgroundOn');
    localStorage.removeItem('entitlement-provider');
    localStorage.removeItem('joii_membership_cache');
    localStorage.removeItem('joii_clinic_profile');
  }

  handleError(error: any) {
    // Handle nested error format (e.g. from Identity Platform API)
    const unwrapped = error?.error ?? error;
    var errorCode = unwrapped.code ?? error.code;
    var errorMessage = unwrapped.message ?? error.message;
    var message = 'Please try again';
    switch (errorCode) {
      case 'auth/user-not-found':
        message = 'User not found';
        break;
      case 'auth/wrong-password':
        message = 'Incorrect Password';
        break;
      case 'auth/cancelled-popup-request':
        message = 'Login Cancelled';
        break;
      case 'auth/popup-closed-by-user':
        message = 'Login Cancelled';
        break;
      case 'auth/weak-password':
        message = 'Password should be at least 6 characters';
        break;
      case 'auth/invalid-tenant-id':
        message = 'Unrecognised Tenant';
        break;
      case 'auth/email-already-in-use':
        message = 'Email address already in use, please login instead';
        break;
      // Error code 47: Firebase Identity Platform bug - blocking function (beforeCreate) fails
      // but returns generic error instead of real message. Often 503 backendError.
      case 'auth/error-code:-47':
      case 'auth/internal-error':
        message = 'Our systems are temporarily unavailable. Please try again in a few moments.';
        break;
      default:
        message = errorMessage;
        break;
    }
    // Handle 503 backend errors / Error code 47 (Firebase Identity Platform blocking function bug)
    if (errorCode === 503 || errorMessage?.includes('Error code: 47') || errorMessage?.includes('backendError')) {
      message = 'Our systems are temporarily unavailable. Please try again in a few moments.';
    }
    throw Error(message);
  }

  isStaffEmail(email: string): boolean {
    return email?.includes('@vet-ai.com') || false;
  }

  updateTenant() {
    this.tenant.set(this.getActiveTenant());
  }

  updateEntitlementProvider() {
    const provider = localStorage.getItem('entitlement-provider');
    this.entitlementProvider.set(provider ?? undefined);
  }

  getTenantById(id: string): Tenant | undefined {
    return environment.tenants.find((t) => t.friendly_id === id);
  }
}
